import {
  BarChart3,
  CalendarDays,
  ClipboardList,
  Radio,
  RefreshCw,
  Settings,
  Shield,
  TriangleAlert,
  WifiOff,
} from "lucide-react";
import type { CSSProperties, ReactNode } from "react";

import type { PlateRelativeWind } from "@/domain/weather";
import { cn } from "@/shared/lib/cn";
import type { AppScreen } from "@/shared/types/contracts";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/shared/ui/sheet";
import { ToastBridge } from "@/shared/ui/toast-bridge";

import { ScorerBanner } from "./ScorerBanner";
import { DingerBanner } from "./DingerBanner";
import { MercyBanner, type MercyBannerState } from "./MercyBanner";

export type ConnectionState =
  "live" | "saving" | "checking" | "offline" | "conflict";

interface AppShellProps {
  children: ReactNode;
  screen: AppScreen;
  teamName: string;
  hasActiveGame: boolean;
  hasActiveSeason?: boolean;
  opponentName: string;
  inning: number;
  batting: boolean;
  ourScore: number;
  opponentScore: number;
  connectionState: ConnectionState;
  onRetryUnknown?: () => void;
  isScorer: boolean;
  scorerName: string | null;
  scoringClaimed: boolean;
  dingerTime?: PlateRelativeWind | null;
  mercyRule?: MercyBannerState | null;
  onClaimScoring: () => void;
  claimScoringPending?: boolean;
  claimScoringUnknown?: boolean;
  onNavigate: (screen: AppScreen) => void;
  manageOpen?: boolean;
  managementContent?: ReactNode;
  onManageOpenChange?: (open: boolean) => void;
  notification?: string | null;
}

const navigation = [
  { id: "game", label: "GAME", icon: Radio },
  { id: "setup", label: "LINEUP", icon: ClipboardList },
  { id: "defense", label: "DEFENSE", icon: Shield },
  { id: "stats", label: "STATS", icon: BarChart3 },
] as const;

const screenGroups: Record<(typeof navigation)[number]["id"], AppScreen[]> = {
  game: ["game"],
  setup: ["setup"],
  defense: ["defense", "defexport"],
  stats: ["stats", "season"],
};

const connectionDetails: Record<
  ConnectionState,
  {
    label: string;
    variant: "success" | "warning" | "danger" | "neutral";
    icon: typeof Radio;
  }
> = {
  live: { label: "Live", variant: "success", icon: Radio },
  saving: { label: "Saving", variant: "warning", icon: Radio },
  checking: { label: "Checking", variant: "warning", icon: RefreshCw },
  offline: { label: "Offline", variant: "neutral", icon: WifiOff },
  conflict: {
    label: "Conflict",
    variant: "danger",
    icon: TriangleAlert,
  },
};

function ShellNavigation({
  className,
  hasActiveGame,
  hasActiveSeason = true,
  onNavigate,
  screen,
}: Pick<
  AppShellProps,
  "hasActiveGame" | "hasActiveSeason" | "onNavigate" | "screen"
> & {
  className?: string;
}) {
  return (
    <nav
      aria-label="Primary"
      className={cn("border-border bg-surface/95 backdrop-blur-md", className)}
    >
      {navigation.map(({ id, label, icon: Icon }) => {
        const active = screenGroups[id].includes(screen);
        const disabled =
          (id === "game" && !hasActiveGame) ||
          ((id === "setup" || id === "defense") && !hasActiveSeason);
        return (
          <button
            key={id}
            type="button"
            aria-current={active ? "page" : undefined}
            disabled={disabled}
            onClick={() => onNavigate(id)}
            className={cn(
              "relative flex min-w-0 items-center justify-center gap-1.5 rounded-[var(--radius-sm)] border border-transparent font-bold text-foreground-muted outline-none transition-colors duration-[var(--motion-fast)] hover:bg-surface-raised hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/70 disabled:pointer-events-none disabled:opacity-35",
              "h-12 flex-1 flex-col text-[0.68rem] leading-none",
              "md:h-9 md:w-full md:flex-none md:flex-row md:justify-start md:px-3 md:text-sm",
              active &&
                "bg-surface-raised text-warning after:absolute after:bg-primary after:content-[''] max-md:after:inset-x-3 max-md:after:bottom-0 max-md:after:h-0.5 md:after:inset-y-1.5 md:after:left-0 md:after:w-0.5",
            )}
          >
            <Icon className="size-4" aria-hidden="true" />
            <span className="truncate">{label}</span>
          </button>
        );
      })}
    </nav>
  );
}

export function AppShell({
  children,
  screen,
  teamName,
  hasActiveGame,
  hasActiveSeason = true,
  opponentName,
  inning,
  batting,
  ourScore,
  opponentScore,
  connectionState,
  onRetryUnknown,
  isScorer,
  scorerName,
  scoringClaimed,
  dingerTime = null,
  mercyRule = null,
  onClaimScoring,
  claimScoringPending = false,
  claimScoringUnknown = false,
  onNavigate,
  manageOpen = false,
  managementContent,
  onManageOpenChange,
  notification = null,
}: AppShellProps) {
  const connection = connectionDetails[connectionState];
  const ConnectionIcon = connection.icon;
  const gameLabel = hasActiveGame
    ? `${batting ? "Top" : "Bottom"} ${inning} · ${ourScore}–${opponentScore} vs ${opponentName}`
    : "Pregame setup";
  const shellStyle = {
    "--shell-header-height": `${3 + (hasActiveGame ? 2.25 : 0) + (dingerTime ? 2.25 : 0) + (mercyRule ? 2.25 : 0)}rem`,
  } as CSSProperties;

  return (
    <div className="min-h-[100svh]" style={shellStyle}>
      <ToastBridge message={notification} />
      <header className="no-print sticky top-0 z-[var(--z-sticky)] border-b border-border bg-background/95 pt-[var(--safe-top)] backdrop-blur-md">
        <div className="mx-auto flex h-12 max-w-[var(--content-width)] items-center gap-3 px-[max(0.75rem,var(--safe-left))] pr-[max(0.75rem,var(--safe-right))]">
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline gap-2">
              <span className="shrink-0 font-mono text-[0.68rem] font-bold tracking-[0.14em] text-primary uppercase">
                scorecard
              </span>
              <span className="truncate text-sm font-bold">{teamName}</span>
            </div>
            <div className="truncate font-mono text-[0.68rem] text-foreground-muted">
              {gameLabel}
            </div>
          </div>
          {connectionState === "checking" && onRetryUnknown ? (
            <Button
              type="button"
              size="xs"
              variant="outline"
              onClick={onRetryUnknown}
              aria-label="Retry unresolved changes"
            >
              <ConnectionIcon
                className="size-3 animate-spin"
                aria-hidden="true"
              />
              CHECKING · RETRY
            </Button>
          ) : (
            <Badge variant={connection.variant} aria-live="polite">
              <ConnectionIcon
                className={cn(
                  "size-3",
                  connectionState === "saving" && "animate-pulse",
                )}
                aria-hidden="true"
              />
              {connection.label}
            </Badge>
          )}
          <Button
            type="button"
            size="icon"
            variant={
              screen === "schedule" || screen === "result"
                ? "selected"
                : "ghost"
            }
            aria-label="Schedule"
            title="Schedule"
            onClick={() => onNavigate("schedule")}
          >
            <CalendarDays className="size-4" aria-hidden="true" />
          </Button>
          {managementContent && onManageOpenChange ? (
            <Button
              type="button"
              size="icon"
              variant={manageOpen ? "selected" : "ghost"}
              aria-label="Manage team"
              title="Manage team"
              onClick={() => onManageOpenChange(true)}
            >
              <Settings className="size-4" aria-hidden="true" />
            </Button>
          ) : null}
        </div>
        <ScorerBanner
          hasActiveGame={hasActiveGame}
          isScorer={isScorer}
          scorerName={scorerName}
          scoringClaimed={scoringClaimed}
          onClaimScoring={onClaimScoring}
          pending={claimScoringPending}
          unknown={claimScoringUnknown}
        />
        <DingerBanner conditions={dingerTime} />
        <MercyBanner
          warning={mercyRule}
          teamName={teamName}
          opponentName={opponentName}
        />
      </header>

      <div className="mx-auto flex w-full max-w-[var(--content-width)] md:items-start">
        <ShellNavigation
          className="no-print sticky top-[calc(var(--shell-header-height)+var(--safe-top))] hidden w-40 shrink-0 flex-col gap-1 border-r p-2 md:flex"
          screen={screen}
          hasActiveGame={hasActiveGame}
          hasActiveSeason={hasActiveSeason}
          onNavigate={onNavigate}
        />
        <main className="min-w-0 flex-1 px-[max(0.625rem,var(--safe-left))] pt-2 pr-[max(0.625rem,var(--safe-right))] pb-20 md:px-4 md:pt-3 md:pb-6">
          {children}
        </main>
      </div>

      <ShellNavigation
        className="no-print fixed inset-x-0 bottom-0 z-[var(--z-navigation)] flex gap-1 border-t px-[max(0.375rem,var(--safe-left))] pt-2 pr-[max(0.375rem,var(--safe-right))] pb-5 shadow-[0_-10px_30px_rgb(0_7_18/35%)] md:hidden"
        screen={screen}
        hasActiveGame={hasActiveGame}
        hasActiveSeason={hasActiveSeason}
        onNavigate={onNavigate}
      />

      {managementContent && onManageOpenChange ? (
        <Sheet open={manageOpen} onOpenChange={onManageOpenChange}>
          <SheetContent side="right">
            <SheetHeader>
              <SheetTitle>Manage team</SheetTitle>
              <SheetDescription>
                Account, roster, invite, presence, and activity.
              </SheetDescription>
            </SheetHeader>
            <SheetBody className="px-3">{managementContent}</SheetBody>
          </SheetContent>
        </Sheet>
      ) : null}
    </div>
  );
}
